<template>
  <div id="doctorsList">
    <!-- Carregando -->
    <template v-if="isLoading">
      <doctors-list-skeleton />
    </template>

    <!-- Vazio -->
    <template v-else-if="!sortedDoctors.length">
      <div class="card state-box">
        <div
          class="ill"
          style="background:var(--color-primary-light);color:var(--color-primary)"
        >
          <svg
            width="36"
            height="36"
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden="true"
          >
            <circle
              cx="11"
              cy="11"
              r="7"
              stroke="currentColor"
              stroke-width="2"
            />
            <path
              d="M21 21l-4-4"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
            />
          </svg>
        </div>
        <template v-if="isExpressMode">
          <h3>Que pena!</h3>
          <p>
            Não achamos profissionais para lhe atender em 30 min, você poderia
            tentar novamente mais tarde? Ficaremos muito felizes em ajudar.
          </p>
        </template>
        <template v-else>
          <h3>Nenhum profissional encontrado</h3>
          <p>
            Encontre profissionais a partir de uma especialidade ou área de
            atuação específica
          </p>
        </template>
      </div>
    </template>

    <!-- Resultados -->
    <template v-else>
      <div class="results-head">
        <span class="cnt">
          <b>{{ totalFound }}</b>
          {{
            totalFound === 1
              ? "profissional encontrado"
              : "profissionais encontrados"
          }}
        </span>
      </div>
      <div class="res-list">
        <doctors-list-card
          v-for="doctor in sortedDoctors"
          :key="doctor.id"
          :doctor="doctor"
          :express="isExpressMode"
        />
      </div>
      <list-pagination
        v-if="!isExpressMode"
        :page="page"
        :total="total"
        label="profissionais"
        @change="goToPage"
      />
    </template>

    <schedule-table-request-modal />
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";

import ScheduleTableRequestModal from "../Schedule/ScheduleTableRequestModal.vue";
import DoctorsListSkeleton from "./DoctorsListSkeleton.vue";
import DoctorsListCard from "./DoctorsListCard.vue";
import ListPagination from "../ListPagination.vue";

export default {
  name: "DoctorsList",
  components: {
    ScheduleTableRequestModal,
    DoctorsListSkeleton,
    DoctorsListCard,
    ListPagination
  },
  props: {
    specialty: {
      type: String,
      default: ""
    },
    fromDate: {
      type: String,
      default: ""
    },
    untilDate: {
      type: String,
      default: ""
    },
    doctorId: {
      type: String,
      default: ""
    },
    sort: {
      type: String,
      default: "rating"
    },
    priceRange: {
      type: String,
      default: null
    },
    doctorName: {
      type: String,
      default: ""
    }
  },
  data() {
    return {
      isLoading: false,
      doctors: [],
      // Paginação no back (10 por página); o total vem no header Total-Count.
      page: 1,
      total: 0
    };
  },
  methods: {
    ...mapActions("doctor", ["fetchDoctors", "fetchDoctorsAvailables"]),
    ...mapActions("user", ["showUser"]),

    async fetchAvailability() {
      try {
        for (const doctor of this.doctors) {
          const query = this.fromDate
            ? `from_date=${this.fromDate}&until_date=${this.untilDate}`
            : "";
          const availability = await this.fetchDoctorsAvailables({
            id: doctor.id,
            query
          });

          if (
            availability &&
            availability.data &&
            availability.data.length > 0
          ) {
            const slot = availability.data.find(
              s => s.slot_date || s.date || s.scheduled_at
            );
            if (slot) {
              doctor.next_available =
                slot.slot_date || slot.date || slot.scheduled_at;
              doctor.next_available_time = this.extractTime(
                slot.start_time || slot.time || slot.scheduled_at
              );
            }
          }
        }
      } catch (error) {
        console.error("Erro ao buscar disponibilidade:", error);
      }
    },

    extractTime(datetime) {
      if (!datetime) return "";
      if (typeof datetime === "string" && datetime.includes(":")) {
        return datetime.substring(0, 5);
      }
      const date = new Date(datetime);
      return date.toLocaleTimeString("pt-BR", {
        hour: "2-digit",
        minute: "2-digit"
      });
    },

    async getDoctors() {
      if (this.isLoading) return;

      const params = {};
      if (this.specialty) params.medical_specialty = this.specialty;
      if (this.fromDate) params.from_date = this.fromDate;
      if (this.untilDate) params.until_date = this.untilDate;
      if (this.doctorId) params.doctor_id = this.doctorId;
      if (this.priceRange) params.price_range = this.priceRange;
      if (this.doctorName) params.doctor_name = this.doctorName;
      // O modo expresso filtra "disponível agora" no front, então precisa da
      // lista completa.
      if (!this.isExpressMode) params.page = this.page;

      try {
        this.isLoading = true;
        const doctors = await this.fetchDoctors(params);
        this.total =
          Number(doctors.headers?.["total-count"]) || doctors.data.length;
        if (this.loggedIn) {
          await this.showUser(this.getCurrentUser.id);
        }
        doctors.data.forEach(data => {
          data.doctor_property.council_description = `${data.doctor_property.council_name} ${data.doctor_property.council_number} ${data.doctor_property.council_uf}`;
        });
        this.doctors = doctors.data;
        // Busca horários de disponibilidade para cada médico
        await this.fetchAvailability();
      } catch {
        this.$toast.error(
          "Erro ao carregar profissionais, tente novamente mais tarde!"
        );
      } finally {
        this.isLoading = false;
      }
    },

    goToPage(page) {
      this.page = page;
      this.getDoctors();
    },

    // Filtro novo começa da primeira página.
    applyFilters() {
      this.page = 1;
      this.getDoctors();
    }
  },
  computed: {
    ...mapGetters("auth", ["loggedIn"]),
    ...mapGetters("user", ["getCurrentUser"]),
    isExpressMode() {
      return this.$route.query.expresso === "1";
    },
    totalFound() {
      return this.isExpressMode ? this.sortedDoctors.length : this.total;
    },
    sortedDoctors() {
      let sorted = [...this.doctors];
      if (this.isExpressMode) {
        sorted = sorted.filter(d => d.available_for_immediate_consultation);
      }
      switch (this.sort) {
        case "price":
          return sorted.sort(
            (a, b) =>
              a.doctor_property.price_cents_and_earning_margin_sum -
              b.doctor_property.price_cents_and_earning_margin_sum
          );
        case "availability":
          return sorted.sort(
            (a, b) => (b.is_online ? 1 : 0) - (a.is_online ? 1 : 0)
          );
        case "rating":
        default:
          return sorted.sort((a, b) => (b.rating || 0) - (a.rating || 0));
      }
    }
  },
  watch: {
    specialty: {
      immediate: true,
      handler() {
        this.applyFilters();
      }
    },
    fromDate() {
      this.applyFilters();
    },
    untilDate() {
      this.applyFilters();
    },
    doctorId() {
      this.applyFilters();
    },
    priceRange() {
      this.applyFilters();
    },
    doctorName() {
      this.applyFilters();
    },
    sort() {}
  }
};
</script>
